import React, { useCallback, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useMediaQuery } from "@mui/material";
import { useSelector } from "react-redux";
import { withTranslation } from "react-i18next";
import PropTypes from "prop-types";
import moment from "moment";
import { omit } from "lodash";
import { REGISTER_BUILDING, FIRE_SAFETY_APPLICATION } from "tagTypes";

import BreadCrumb from "Components/Common/BreadCrumb";
import BaseTable from "Components/Common/BaseTable";
import { fireSafetyAdminRoles } from "common/constant";
import FireSafetyApplicationConsumerAction from "./FireSafetyApplicationConsumerAction";
import FireSafetyApplicationAdminActions from "./FireSafetyApplicationAdminActions";
import FireSafetyBuildingViewModal from "Components/FireSafetyBuildingViewModal";
import FilterFireSafetyApplications from "./FilterFireSafetyApplications";
import FireSafetyViewBuildingSchedules from "./FireSafetyViewBuildingSchedules";

import { convertUTCDateToLocalDate } from "utils/convertUTCDateToLocalDate";
import { formatUtilityAddress } from "utils/formatUtilityAddress";
import { isAuthorizedUser } from "utils";

const RECORDS_PER_PAGE = 10;

const FireSafetyApplicationRequestList = ({
  t,
  isBuildingObservationSideBarSelected = false,
}) => {
  const [searchParams, setSearchParams] = useSearchParams();

  const { roles } = useSelector((state) => state.Login);

  const applicationId = searchParams.get("applicationId");
  const applicationIdForViewBuildingObservation = searchParams.get(
    "buildingObservationId"
  );

  const [query, setQuery] = useState({});
  const lg = useMediaQuery("(min-width: 768px) and (max-width: 1399px)");
  const md = useMediaQuery("(min-width: 576px) and (max-width: 767px)");
  const sm = useMediaQuery("(max-width: 575px)");

  const [isResetPageRegistered, setIsResetPageRegistered] = useState(false);

  const resetPageRef = useRef();

  const handleResetPage = useCallback(resetPageRef.current, [
    isResetPageRegistered,
  ]);

  const viewApplicationBtnHandler = (applicationId) => {
    searchParams.set("applicationId", applicationId);
    setSearchParams(searchParams);
  };

  const columns = [
    {
      title: "User Name",
      field: "userName",
    },
    {
      title: "Building Name",
      field: "fireSafetyAddress.building.name",
    },
    {
      title: "Address",
      renderCell: ({ fireSafetyAddress }) => {
        const address = formatUtilityAddress(omit(fireSafetyAddress, "floors"));
        if (address) {
          return (
            <p className="m-0">
              {address}, {fireSafetyAddress?.city?.name ?? ""}
              {fireSafetyAddress?.pinCode
                ? ` - ${fireSafetyAddress.pinCode}`
                : ""}
            </p>
          );
        }

        return "--";
      },
      columnProps: { style: { width: "350px", minWidth: "350px" } },
    },
    {
      title: "FSCA No.",
      field: "certificateApprovalNumber",
    },
    {
      title: "Submission Date",
      renderCell: ({ submissionDate }) =>
        moment(submissionDate).isValid()
          ? convertUTCDateToLocalDate(submissionDate).format("DD-MM-YYYY")
          : "N/A",
    },
    {
      title: "Status",
      renderCell: (item) => item?.buildingInformationStatus?.name,
    },
    {
      title: "Actions",
      renderCell: (item) => {
        return isAuthorizedUser(fireSafetyAdminRoles, roles) ? (
          <FireSafetyApplicationAdminActions
            item={item}
            viewApplicationBtnHandler={viewApplicationBtnHandler}
            isBuildingObservationSideBarSelected={
              isBuildingObservationSideBarSelected
            }
          />
        ) : (
          <FireSafetyApplicationConsumerAction item={item} />
        );
      },
    },
  ];

  const serverSidePaginationConfig = useMemo(
    () => ({
      endpoint: "/building-information",
      tags: [REGISTER_BUILDING, FIRE_SAFETY_APPLICATION],
      query,
    }),
    [query]
  );

  return (
    <>
      {!!applicationId && (
        <FireSafetyBuildingViewModal
          applicationId={applicationId}
          handleClose={() => {
            searchParams.delete("applicationId");
            setSearchParams(searchParams);
          }}
          isBuildingObservationSideBarSelected={
            isBuildingObservationSideBarSelected
          }
        />
      )}

      {!!applicationIdForViewBuildingObservation && (
        <FireSafetyViewBuildingSchedules
          applicationId={applicationIdForViewBuildingObservation}
          handleClose={() => {
            searchParams.delete("buildingObservationId");
            setSearchParams(searchParams);
          }}
        />
      )}

      <div>
        <BreadCrumb
          title={t(
            `Building ${
              isBuildingObservationSideBarSelected
                ? "Observation"
                : "Information"
            } Request Details`
          )}
        />
        <FilterFireSafetyApplications
          setQuery={setQuery}
          resetPage={handleResetPage}
          isBuildingObservationSideBarSelected={
            isBuildingObservationSideBarSelected
          }
        />
        <BaseTable
          columns={columns}
          pageSize={RECORDS_PER_PAGE}
          paginationType="serverSide"
          serverSidePaginationConfig={serverSidePaginationConfig}
          registerResetPage={(cb) => {
            resetPageRef.current = cb;
            setIsResetPageRegistered((prev) => !prev);
          }}
          maxHeight={
            sm
              ? "calc(100vh - 400px)"
              : md
              ? "calc(100vh - 360px)"
              : lg
              ? "calc(100vh - 120px)"
              : "calc(100vh - 170px)"
          }
        />
      </div>
    </>
  );
};

FireSafetyApplicationRequestList.propTypes = {
  t: PropTypes.func,
  isBuildingObservationSideBarSelected: PropTypes.bool,
};

export default withTranslation()(FireSafetyApplicationRequestList);
